'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import regions, {
  countryToRegion,
  type RegionCode,
  type RegionConfig,
} from '@/lib/geo';

/* ── Context shape ── */

interface GeoContextValue {
  region: RegionConfig;
  countryCode: string;       // raw ISO code detected
  loading: boolean;          // true while detecting
  setRegion: (code: RegionCode) => void; // manual override
}

const GeoContext = createContext<GeoContextValue>({
  region: regions.GLOBAL,
  countryCode: '',
  loading: true,
  setRegion: () => {},
});

export const useGeo = () => useContext(GeoContext);

/* ── Cookie helpers ── */

const COOKIE_NAME = 'op_geo';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
}

/* ── Detection logic ── */

/**
 * Detection priority:
 * 1. URL param `?country=IN`  (dev/testing + your convenience link)
 * 2. Cookie `op_geo` set by middleware or previous visit
 * 3. IP geolocation via geojs.io (free, no key). It must be the ONLY lookup
 *    host: the CSP connect-src allows just get.geojs.io (src/lib/csp.ts), so
 *    any other API is refused by the browser (ipapi.co was, 22 Sep 2026).
 * 4. Default → GLOBAL
 */
async function detectCountry(): Promise<string> {
  // 1. URL param (middleware strips it, but handle race)
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const paramCountry = params.get('country');
    if (paramCountry && paramCountry.toLowerCase() !== 'reset') {
      return paramCountry.toUpperCase();
    }
  }

  // 2. Cookie (set by middleware or previous detection)
  const cookieVal = getCookie(COOKIE_NAME);
  if (cookieVal) return cookieVal;

  // 3. IP-based geolocation: geojs.io (unlimited, no key)
  try {
    const res = await fetch('https://get.geojs.io/v1/ip/country.json', { cache: 'no-store' });
    if (res.ok) {
      const data = await res.json();
      if (data?.country) return data.country.toUpperCase();
    }
  } catch {
    /* silent — fall through to default */
  }

  return '';
}

/* ── Provider ── */

export default function GeoProvider({ children }: { children: ReactNode }) {
  const [countryCode, setCountryCode] = useState('');
  const [regionCode, setRegionCode] = useState<RegionCode>('GLOBAL');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectCountry().then((code) => {
      const resolved = countryToRegion(code);
      setCountryCode(code);
      setRegionCode(resolved);
      setCookie(COOKIE_NAME, code || 'GLOBAL');
      setLoading(false);
    });
  }, []);

  const setRegion = (code: RegionCode) => {
    setRegionCode(code);
    setCountryCode(code);
    setCookie(COOKIE_NAME, code);
  };

  return (
    <GeoContext.Provider
      value={{
        region: regions[regionCode],
        countryCode,
        loading,
        setRegion,
      }}
    >
      {children}
    </GeoContext.Provider>
  );
}
